// import firebase from 'firebase/app';
import localStorage from 'localstorage-ttl';
import { flow, getRoot, Instance, types } from 'mobx-state-tree';

import appService from '../services/appService';
import spotifyConnectService from '../services/spotifyConnectService';
import spotifyService from '../services/spotifyService';
import config from '../utils/config';
import firebaseService from './../services/firebaseService';
import { AuthProviders, MusicProviders, UserRoles } from './../types/shared';
import { IUserModel } from './models/UserModel';
import { IPlaybackStore } from './PlaybackStore';
import { IRootStore } from './RootStore';

const AuthStore = types
    .model('AuthStore', {
        isAuthenticated: types.optional(types.boolean, false),
    })
    .volatile(() => ({
        role: UserRoles.ANONYMOUS,
        authMethod: AuthProviders.NONE,
    }))
    .actions(self => {
        const rootStore: IRootStore = getRoot(self);

        const setAuthRole = (role: UserRoles) => {
            self.role = role;
        };

        const signIn = () => {
            appService.signIn();
        };

        // TODO: If User is a GUEST, remove it from playlist
        const signOut = flow(function*() {
            yield firebaseService.signOut();
            yield appService.signOut();
            self.isAuthenticated = false;
            spotifyService.setAccessToken('');
            spotifyConnectService.disconnect();
            const redirectPath = self.role === UserRoles.HOST ? '/' : '/signin-party';
            window.localStorage.removeItem(config.LOCAL_STORAGE_APP_KEY);
            window.location.href = redirectPath;
        });

        // TODO: Load and set everything up when refresh token. Now is not setting the configuration propertly
        const getAccessToken = flow(function* getAccessToken(code: string, state: string) {
            try {
                const playbackStore: IPlaybackStore = rootStore.playback;
                const response = yield appService.getAccessToken(code, state);
                yield firebaseService.signIn(response.data.fbToken);
                rootStore.config.setConfig(response.data.playlistConfig);
                rootStore.me.setUser(response.data.profile);
                spotifyService.setAccessToken(response.data.accessToken);
                self.authMethod = AuthProviders.SPOTIFY;
                // Load playback and queue data from db
                yield playbackStore.loadDataFromDB();
                yield rootStore.users.loadUsersFromDB();
                self.isAuthenticated = true;
            } catch (error) {
                console.log(error);
                signOut();
            }
        }) as (code: string, state: string) => Promise<void>;

        // TODO: call this method when access token is not valid anymore
        const refreshSpotifyUserAccessToken = flow(function*() {
            try {
                if (spotifyService.getAccessToken()) {
                    return;
                }
                const response = yield appService.refreshSpotifyUserAccessToken();
                spotifyService.setAccessToken(response.data.accessToken);
            } catch (error) {
                console.log(error);
                signOut();
            }
        });

        const getGuestSpotifyAccessToken = flow(function*() {
            const spotifyAppAccount = yield appService.signInSpotifyApp();
            spotifyService.setAccessToken(spotifyAppAccount.data.accessToken);
        });
        const signInGuesWithSpotify = () => {
            appService.signInSpotifyGuest();
        };
        // TODO: Refactor this action, lot of responsabilities for a single function
        const signInGuest = flow(function*(provider: AuthProviders, code = '', state = '') {
            try {
                self.authMethod = provider;
                console.log('self.authMethod', self.authMethod);
                let guestUser: IUserModel | null = null;
                if (code && state) {
                    const response = yield appService.getSpotifyGuestAccessToken(code, state);
                    yield firebaseService.signIn(response.data.fbToken);
                    const user = response.data.profile;
                    if (user === null) {
                        console.log('auth user error');
                        return;
                    }
                    spotifyService.setAccessToken(response.data.accessToken);
                    guestUser = {
                        id: user.id,
                        displayName: user.displayName || '',
                        photoURL: user.photoURL || '',
                        email: user.email,
                    };
                } else {
                    const response: firebase.auth.UserCredential = yield firebaseService.signInGuest(provider);
                    const user = response.user;
                    yield getGuestSpotifyAccessToken();
                    if (user === null) {
                        console.log('auth user error');
                        return;
                    }
                    guestUser = {
                        id: user.uid,
                        displayName: user.displayName || '',
                        photoURL: user.photoURL || '',
                        email: '',
                    };
                }
                rootStore.me.setUser(guestUser);
                const link: string = localStorage.get(config.LOCAL_STORAGE_PARTY_LINK_TO_REDIRECT);
                const linkParts = link.split('/');
                let isLinkValid = false;
                if (linkParts.length !== 4) {
                    return;
                }
                isLinkValid = yield rootStore.config.validatePartyLink({
                    userId: linkParts[2],
                    playlistId: linkParts[3],
                });
                if (!isLinkValid) {
                    window.location.href = '/party-not-found';
                    return;
                }
                self.isAuthenticated = true;
                const partyId = `${MusicProviders.SPOTIFY}:${linkParts[2]}:${linkParts[3]}`;
                rootStore.config.setPartyUID(partyId);
                yield rootStore.playback.loadDataFromDB();
                yield rootStore.users.loadUsersFromDB();
                yield rootStore.users.addUserGuestToDB(guestUser);
            } catch (error) {
                console.log('error: ', error);
            }
        }) as (provider: AuthProviders, code?: string, state?: string) => Promise<void>;

        // TODO: call this action to refresh the Guest Spotify App token when expires
        // TODO: if user guest auth provider is Spotify, then use the refreshSpotifyUserAccessToken action
        const refreshSpotifyAppAccessToken = flow(function*() {
            try {
                if (spotifyService.getAccessToken()) {
                    return;
                }
                yield getGuestSpotifyAccessToken();
            } catch (error) {
                console.log(error);
                signOut();
            }
        });

        const getSpotifyGuestAccessToken = flow(function* getAccessToken(code: string, state: string) {
            try {
                const playbackStore: IPlaybackStore = rootStore.playback;
                const response = yield appService.getSpotifyGuestAccessToken(code, state);
                yield firebaseService.signIn(response.data.fbToken);
                rootStore.me.setUser(response.data.profile);
                spotifyService.setAccessToken(response.data.accessToken);
                // Load playback and queue data from db
                yield playbackStore.loadDataFromDB();
                self.isAuthenticated = true;
            } catch (error) {
                console.log(error);
                signOut();
            }
        }) as (code: string, state: string) => Promise<void>;

        return {
            signIn,
            signOut,
            getAccessToken,
            refreshSpotifyUserAccessToken,
            signInGuest,
            signInGuesWithSpotify,
            setAuthRole,
            refreshSpotifyAppAccessToken,
            getSpotifyGuestAccessToken,
        };
    });

export interface IAuthStore extends Instance<typeof AuthStore> {}

export default AuthStore;
